import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, ParamMap } from '@angular/router';
import { Location } from '@angular/common';
import {
  GwrGebaeudeDetailEntry,
  GwrGebaeudeListEntry,
  GwrGebaeudeSearchByIdResponse,
  GwrgebaeudeService,
  GwrGrundstueckListEntry,
  GwrWohnungListEntry
} from '../../../core/gwr-api';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { keysToTranslate } from 'i18n/keys-to-translate';
import { switchMap } from 'rxjs/operators';
import { AbstractComponent } from '../../../shared/components/abstract-component/abstract.component';
import { ListLoader } from '../../../shared/components/list-loader/list-loader';
import { ListLoaderPreloaded } from '../../../shared/components/list-loader/list-loader-preloaded';
import { CapAlertService } from '../../../core/extension/exceptions/cap-alert.service';
import { CapAlertLevel } from '../../../core/extension/exceptions/cap-alert-level.enum';
import { environment } from '../../../../environments/environment';
import { GwrStatus } from '../../../shared/enum/GwrStatus';
import { ContentHeaderComponent } from '../../../shared/components/content-header/content-header.component';
import { TitleComponent } from '../../../shared/components/title/title.component';
import { KopfdatenelementComponent } from '../../../shared/widgets/kopfdatenelement/kopfdatenelement.component';
import { NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { GwrWohnungTableComponent } from '../../gwr-wohnung/gwr-wohnung-list/gwr-wohnung-table.component';
import { GwrGrundstueckTableComponent } from '../../gwr-grundstueck-list/gwr-grundstueck-table.component';
import { CapSpinnerSmallComponent } from '../../../shared/components/cap-spinner-small/cap-spinner-small.component';
import { DecimalHochkommaPipe } from '../../../shared/pipes/simple/decimalHochkomma.pipe';
import { GwrBaujahrPeriodePipe } from '../gwr-baujahr-periode-pipe';
import { KompaktGwrGebaeudeeingangsadressePipe } from '../kompakt-gebaeudeeingangsadresse-pipe';
import GebaeudeKategorieEnum = GwrGebaeudeListEntry.GebaeudeKategorieEnum;
import GebaeudeStatusEnum = GwrGebaeudeListEntry.GebaeudeStatusEnum;
import GebaeudeKlasseEnum = GwrGebaeudeDetailEntry.GebaeudeKlasseEnum;

@Component({
  selector: 'cap-gwr-gebaeude-detail',
  templateUrl: './gwr-gebaeude-detail.component.html',
  styleUrls: ['./gwr-gebaeude-detail.component.scss'],
  imports: [
    ContentHeaderComponent,
    TitleComponent,
    KopfdatenelementComponent,
    NgbTooltip,
    GwrWohnungTableComponent,
    GwrGrundstueckTableComponent,
    CapSpinnerSmallComponent,
    DecimalHochkommaPipe,
    GwrBaujahrPeriodePipe,
    KompaktGwrGebaeudeeingangsadressePipe,
    TranslatePipe
  ]
})
export class GwrGebaeudeDetailComponent extends AbstractComponent implements OnInit {
  gebaeude: GwrGebaeudeDetailEntry;

  grundstueckListLoader: ListLoader<GwrGrundstueckListEntry>;
  wohnungListLoader: ListLoader<GwrWohnungListEntry>;

  externerGwrLink: string = environment.gwrServerUrl + '?egid=';

  constructor(
    private gebaeudeService: GwrgebaeudeService,
    private translate: TranslateService,
    private location: Location,
    private route: ActivatedRoute,
    private capAlertService: CapAlertService
  ) {
    super();
    keysToTranslate<string>([
      'Abbruchjahr',
      'AnzahlWohnungen',
      'AnzahlGeschosse',
      'AnzahlWohnraeume',
      'Baujahr',
      'Renovationsjahr',
      'GEBAEUDEKATEGORIE',
      'GEBAEUDEKLASSE',
      'GEBAEUDESTATUS'
    ]);
    keysToTranslate<GebaeudeStatusEnum | GebaeudeKlasseEnum | GebaeudeKategorieEnum>([
      'PROJEKTIERT',
      'BEWILLIGT',
      'IM_BAU',
      'BESTEHEND',
      'NICHT_NUTZBAR',
      'ABGEBROCHEN',
      'NICHT_REALISIERT',
      'GEBAEUDE_MIT_EINER_WOHNUNG',
      'GEBAEUDE_MIT_ZWEI_WOHNUNGEN',
      'GEBAEUDE_MIT_DREI_ODER_MEHR_WOHNUNGEN',
      'WOHNGEBAEUDE_FUER_GEMEINSCHAFTEN',
      'HOTELGEBAEUDE',
      'ANDERE_GEBAEUDE_FUER_KURZFRISTIGE_BEHERBERGUNGEN',
      'BUEROGEBAEUDE',
      'GROSS_UND_EINZELHANDELSGEBAEUDE',
      'RESTAURANTS_UND_BARS_IN_GEBAEUDEN_OHNE_WOHNNUTZUNG',
      'BAHNHOEFE_ABFERTIGUNGSGEBAEUDE_FERNSPRECHVERMITTLUNGSZENTRALEN',
      'GARAGENGEBAEUDE',
      'INDUSTRIEGEBAEUDE',
      'BEHAELTER_SILOS_UND_LAGERGEBAEUDE',
      'GEBAEUDE_FUER_KULTUR_UND_FREIZEITZWECKE',
      'MUSEEN_BIBLIOTHEKEN',
      'SCHUL_UND_HOCHSCHULGEBAEUDE_FORSCHUNGSEINRICHTUNGEN',
      'KRANKENHAEUSER_UND_FACHEINRICHTUNGEN_DES_GESUNDHEITSWESENS',
      'SPORTHALLEN',
      'LANDWIRTSCHAFTLICHE_BETRIEBSGEBAEUDE',
      'KIRCHEN_UND_SONSTIGE_KULTGEBAEUDE',
      'DENKMAELER_ODER_UNTER_DENKMALSCHUTZ_STEHENDE_BAUWERKE',
      'SONSTIGE_HOCHBAUTEN',
      'ANDERE_GEBAEUDE_FUER_DIE_KOLLEKTIVE_UNTERKUNFT',
      'GEBAEUDE_FUER_DIE_TIERHALTUNG',
      'GEBAEUDE_FUER_DEN_PFLANZENBAU',
      'ANDERE_LANDWIRTSCHAFTLICHE_GEBAEUDE',
      'PROVISORISCHE_UNTERKUNFT',
      'GEBAEUDE_MIT_AUSSCHLIESSLICHER_WOHNNUTZUNG',
      'ANDERE_WOHNGEBAEUDE',
      'GEBAEUDE_MIT_TEILWEISER_WOHNNUTZUNG',
      'GEBAEUDE_OHNE_WOHNNUTZUNG',
      'SONDERBAU'
    ]);
  }

  ngOnInit() {
    this.addSub(
      this.route.paramMap
        .pipe(
          switchMap((paramMap: ParamMap) => {
            const egid = paramMap.get('egid');
            if (egid) {
              return this.gebaeudeService.loadGebaeudeByEgid(+egid);
            }
          })
        )
        .subscribe({
          next: (response) => this.fillGebaeudeFrom(response),
          error: (err) => this.handleError(err)
        })
    );
  }

  initGrundstueckListLoader() {
    this.grundstueckListLoader = new ListLoaderPreloaded<GwrGrundstueckListEntry>(15, 1, this.gebaeude.grundstuecke);
    this.addLoader(this.grundstueckListLoader);
  }

  initWohnungListLoader() {
    this.wohnungListLoader = new ListLoaderPreloaded(15, 1, this.gebaeude.wohnungen);
    this.addLoader(this.wohnungListLoader);
  }

  navigateBackToSearch() {
    this.location.back();
  }

  hasExternalGWR(): boolean {
    return environment.gwrServerUrl !== null;
  }

  openExternalGWRLink(egid: string) {
    if (this.hasExternalGWR()) {
      window.open(this.externerGwrLink + egid, '_blank');
    }
  }

  private fillGebaeudeFrom(response: GwrGebaeudeSearchByIdResponse) {
    const wsStatus = response.gwrStatusCode;
    if (wsStatus.statusCode !== GwrStatus.OK) {
      const alertText = this.translate.instant('FehlerBeimAufrufDesWebservice');
      this.capAlertService.warning(`${alertText}: ${wsStatus.statusCode} (${wsStatus.text})`, CapAlertLevel.CONTENT_HEADER, true);
      this.navigateBackToSearch();
    }
    this.gebaeude = response.result;
    this.initGrundstueckListLoader();
    this.initWohnungListLoader();
  }

  private handleError(err) {
    this.navigateBackToSearch();
    throw err;
  }
}
